import { useState, type ReactNode } from 'react';
import { Badge, Button, EmptyState, InfoHint, Panel } from '../../components/ui';
import { isAllowedImageSrc } from '../../components/RichText';
import {
  CLARITY_PROJECT_ID,
  DM_BATCH_DAYS,
  DM_VERSIONS,
  GA4_PROPERTY_ID,
  PAGES,
  RANGES,
} from './pagePulseConfig';
import {
  CLARITY_INSIGHTS,
  PULSE_DEFINITIONS,
  TILE_DEFINITIONS,
  chartScale,
  clarityHistoryNote,
  clarityScreen,
  clarityTiles,
  copiedAt,
  dmTable,
  ga4Duration,
  ga4Screen,
  gaKpiTiles,
  lastUpdate,
  screenCopies,
  seriesPoints,
  shortDay,
  sourceRows,
  total,
  updatedAsOf,
  vidalyticsScreen,
  type KpiTile,
  type PageFilter,
  type PulseDefinitionKey,
  type PulseTab,
  type ScreenCopy,
  type ScreenGroup,
} from './pagePulseMetrics';
import {
  downloadPulseCsv,
  downloadPulseText,
  exportAll,
  exportClarity,
  exportKpis,
  exportPages,
  exportSources,
} from './pagePulseExport';
import type { PulsePayload } from './pagePulseApi';
import './pagePulse.css';

function ExportButton({ name, rows }: { name: string; rows: string[][] }) {
  return (
    <Button size="sm" variant="secondary" onClick={() => downloadPulseCsv(`${name}.csv`, rows)}>
      Export
    </Button>
  );
}
/** A label with GA4's or Clarity's own (i) explanation beside it. */
function Term({
  label,
  term,
  align = 'start',
}: {
  label: string;
  term: PulseDefinitionKey;
  align?: 'start' | 'end';
}) {
  return (
    <>
      {label}
      <InfoHint label={label} text={PULSE_DEFINITIONS[term].text} align={align} />
    </>
  );
}
function Tiles({
  tiles,
  terms = TILE_DEFINITIONS,
}: {
  tiles: KpiTile[];
  terms?: Record<string, PulseDefinitionKey>;
}) {
  return (
    <div className="tracker-kpi-grid">
      {tiles.map((tile, index) => {
        const term = terms[tile.label];
        return (
          <div className="tracker-kpi-card" key={`${tile.label}-${index}`}>
            <p className="tracker-kpi-label">
              {term === undefined ? (
                tile.label
              ) : (
                <Term label={tile.label} term={term} align={index % 2 === 1 ? 'end' : 'start'} />
              )}
            </p>
            <p className="tracker-kpi-value">{tile.value}</p>
            {tile.note === undefined ? null : <p className="tracker-kpi-sub">{tile.note}</p>}
          </div>
        );
      })}
    </div>
  );
}
const breakdownRows = (
  payload: PulsePayload,
  page: PageFilter,
  name: string,
  source: 'ga4' | 'clarity' = 'ga4',
) => {
  const values = new Map<string, number>();
  for (const row of payload.rows.filter(
    (row) => row.source === source && (page === 'all' || row.page_path === page),
  ))
    for (const [key, value] of Object.entries(
      (row.breakdowns[name] ?? {}) as Record<string, unknown>,
    ))
      values.set(key, (values.get(key) ?? 0) + (typeof value === 'number' ? value : 0));
  return [...values].sort((a, b) => b[1] - a[1]);
};

/** GA4's line chart: dated x axis, y gridlines with values, legend, readout. */
function ViewsChart({ series }: { series: ReturnType<typeof seriesPoints> }) {
  const scale = chartScale(series);
  const days = series[0]?.points.map((point) => point.day) ?? [];
  const [active, setActive] = useState<number | null>(null);
  const pick = (clientX: number, box: DOMRect) =>
    days.length === 0 || box.width === 0
      ? null
      : Math.min(
          days.length - 1,
          Math.max(0, Math.round(((clientX - box.left) / box.width) * (days.length - 1))),
        );
  const shown = active === null ? undefined : days[active];
  return (
    <figure className="page-pulse-chart">
      <figcaption className="page-pulse-chart-title">
        <Term label="Views" term="views" /> by Page path and screen class over time
      </figcaption>
      <div className="page-pulse-chart-frame">
        <div className="page-pulse-chart-y" aria-hidden="true">
          {scale.ticks.map((tick) => (
            <span key={tick} style={{ top: `${scale.y(tick)}%` }}>
              {tick}
            </span>
          ))}
        </div>
        <div
          className="page-pulse-chart-plot"
          role="img"
          tabIndex={0}
          aria-label={`Views per day, ${days.length} days. Use the left and right arrow keys to read each day.`}
          onPointerMove={(event) =>
            setActive(pick(event.clientX, event.currentTarget.getBoundingClientRect()))
          }
          onPointerDown={(event) =>
            setActive(pick(event.clientX, event.currentTarget.getBoundingClientRect()))
          }
          onPointerLeave={(event) => {
            if (event.pointerType === 'mouse') setActive(null);
          }}
          onKeyDown={(event) => {
            if (days.length === 0) return;
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            const step = event.key === 'ArrowLeft' ? -1 : 1;
            setActive((current) =>
              Math.min(
                Math.max((current ?? (step > 0 ? -1 : days.length)) + step, 0),
                days.length - 1,
              ),
            );
          }}
          onBlur={() => setActive(null)}
        >
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {scale.ticks.map((tick) => (
              <line
                key={tick}
                className="page-pulse-grid"
                x1="0"
                x2="100"
                y1={scale.y(tick)}
                y2={scale.y(tick)}
              />
            ))}
            {active === null ? null : (
              <line
                className="page-pulse-cursor"
                x1={scale.x(active)}
                x2={scale.x(active)}
                y1="0"
                y2="100"
              />
            )}
            {series.map((line, index) => (
              <polyline
                key={line.path}
                fill="none"
                points={line.points
                  .map((point, pointIndex) => `${scale.x(pointIndex)},${scale.y(point.value)}`)
                  .join(' ')}
                className={`page-pulse-line page-pulse-line--${index}`}
              />
            ))}
          </svg>
          {shown === undefined ? null : (
            <div className="page-pulse-readout" role="status">
              <strong>{shortDay(shown)}</strong>
              {series.map((line, index) => (
                <span key={line.path}>
                  <i className={`page-pulse-swatch page-pulse-line--${index}`} />
                  {line.path}: {line.points[active ?? 0]?.value ?? 0}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="page-pulse-chart-x" aria-hidden="true">
          {scale.xLabels.map((label) => (
            <span key={label.x} style={{ left: `${label.x}%` }}>
              {label.text}
            </span>
          ))}
        </div>
      </div>
      <ul className="page-pulse-legend" aria-label="Legend">
        {series.map((line, index) => (
          <li key={line.path}>
            <i className={`page-pulse-swatch page-pulse-line--${index}`} />
            {line.path}
          </li>
        ))}
      </ul>
    </figure>
  );
}

function TwoColumn({
  name,
  term,
  rows,
  valueTerm = 'sessions',
}: {
  name: string;
  term: PulseDefinitionKey;
  rows: [string, number | string][];
  valueTerm?: PulseDefinitionKey;
}) {
  return (
    <div className="page-pulse-table-scroll page-pulse-table-scroll--two">
      <table className="ui-table">
        <thead>
          <tr>
            <th>
              <Term label={name} term={term} />
            </th>
            <th>
              <Term label="Sessions" term={valueTerm} align="end" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2}>No data available</td>
            </tr>
          ) : (
            rows.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td>{value}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/** Copied pictures. Only a host the Hub already trusts is drawn; anything else stays a slot. */
function ScreenImages({ images }: { images: ScreenGroup['images'] }) {
  if (images.length === 0) return null;
  return (
    <div className="page-pulse-images">
      {images.map((image, index) =>
        isAllowedImageSrc(image.src) ? (
          <figure key={`${image.label}-${index}`}>
            <img src={image.src} alt={image.label} loading="lazy" />
            <figcaption className="page-pulse-partial">{image.label}</figcaption>
          </figure>
        ) : (
          <p className="page-pulse-partial" key={`${image.label}-${index}`}>
            {image.label}: picture not shown yet.
          </p>
        ),
      )}
    </div>
  );
}
/** Copied items with no fixed box of their own, one panel per tab. No (i): no source wording for them. */
function ScreenGroups({ groups }: { groups: ScreenGroup[] }) {
  return (
    <>
      {groups.map((group) => (
        <Panel key={group.tab} title={group.tab}>
          {group.tiles.length === 0 ? null : <Tiles tiles={group.tiles} terms={{}} />}
          <ScreenImages images={group.images} />
        </Panel>
      ))}
    </>
  );
}
function ExternalLinks({ links }: { links: [string, string][] }) {
  return (
    <div className="page-pulse-links">
      {links.map(([label, href]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer">
          {label}
        </a>
      ))}
    </div>
  );
}
const CLARITY_LINKS: [string, string][] = [
  ['Open recordings', 'https://admin.shopify.com/store/4ocean/apps/microsoft-clarity-prod/app'],
  ['Open heatmaps', 'https://admin.shopify.com/store/4ocean/apps/microsoft-clarity-prod/app'],
];
const VIDALYTICS_LINKS: [string, string][] = [['Open Vidalytics', 'https://apps.vidalytics.com']];
const TABS: [PulseTab, string][] = [
  ['ga4', 'GA4'],
  ['clarity', 'Clarity'],
  ['vidalytics', 'Vidalytics'],
];

/** What Claude in Chrome copied from Vidalytics: account totals, then one block per video. */
function VidalyticsTab({ payload }: { payload: PulsePayload }) {
  const view = vidalyticsScreen(payload);
  return (
    <>
      {view.takenAt === null ? (
        <p className="page-pulse-partial">Nothing to show until the first Vidalytics copy.</p>
      ) : (
        <div className="page-pulse-screen page-pulse-screen--vidalytics">
          <ScreenGroups groups={view.account} />
          {view.videos.map((entry) => (
            <Panel key={entry.video} title={entry.video}>
              {entry.groups.map((group) => (
                <div key={group.tab}>
                  <p className="page-pulse-partial">{group.tab}</p>
                  {group.tiles.length === 0 ? null : <Tiles tiles={group.tiles} terms={{}} />}
                  <ScreenImages images={group.images} />
                </div>
              ))}
            </Panel>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * What Claude in Chrome copied from Clarity's Dashboard for one page, in the
 * same boxes and order as Clarity's screen (BRIEF.md section 6). Values are
 * shown exactly as copied; nothing is added up or recalculated.
 */
function ClarityCopy({ copy, showPage }: { copy: ScreenCopy; showPage: boolean }) {
  const view = clarityScreen(copy.items);
  return (
    <div className="page-pulse-screen page-pulse-screen--clarity">
      <p className="page-pulse-partial">
        Copied at {copiedAt(copy.takenAt) ?? '--'} PHT from Clarity
        {showPage ? `, ${copy.page}` : ''}. Dashboard &gt; Overview as Clarity shows it.
      </p>
      <Panel title="Overview">
        <Tiles tiles={view.top} terms={{ ...TILE_DEFINITIONS, Sessions: 'claritySessions' }} />
      </Panel>
      <Panel title="Users overview">
        <Tiles tiles={view.users} terms={{}} />
      </Panel>
      <Panel title="Insights">
        <Tiles
          tiles={view.insights}
          terms={Object.fromEntries(CLARITY_INSIGHTS.map(([label, , term]) => [label, term]))}
        />
      </Panel>
      <Panel title="Smart events">
        {view.smartEvents.length === 0 ? (
          <p>No data available</p>
        ) : (
          <TwoColumn
            name="Smart events"
            term="smartEvents"
            rows={view.smartEvents}
            valueTerm="claritySessions"
          />
        )}
      </Panel>
      <ScreenGroups groups={view.more} />
    </div>
  );
}
/** What was copied from GA4's reports for one page, one panel per report. */
function Ga4Copy({ copy, showPage }: { copy: ScreenCopy; showPage: boolean }) {
  const reports = ga4Screen(copy.items);
  if (reports.length === 0) return null;
  return (
    <div className="page-pulse-screen page-pulse-screen--ga4">
      <p className="page-pulse-partial">
        Copied at {copiedAt(copy.takenAt) ?? '--'} PHT from GA4
        {showPage ? `, ${copy.page}` : ''}.
      </p>
      {reports.map((report) => (
        <Panel key={report.tab} title={report.tab}>
          <Tiles tiles={report.tiles} />
        </Panel>
      ))}
    </div>
  );
}

export function PagePulseView({
  payload,
  days,
  onDays,
  mode,
  shareSlot,
}: {
  payload: PulsePayload;
  days: (typeof RANGES)[number];
  onDays: (days: (typeof RANGES)[number]) => void;
  mode: 'hub' | 'share';
  shareSlot?: ReactNode;
}) {
  const [page, setPage] = useState<PageFilter>('all');
  const [tab, setTab] = useState<PulseTab>('ga4');
  const sources = sourceRows(payload, page);
  const dm = dmTable(payload, page, DM_VERSIONS);
  const series = seriesPoints(payload, page, DM_BATCH_DAYS);
  const selected = page === 'all' ? PAGES : PAGES.filter((item) => item.path === page);
  const claritySessions = total(payload, page, 'sessions', 'clarity');
  const clarityProject: string = CLARITY_PROJECT_ID;
  const smartEvents = breakdownRows(payload, page, 'smart_events', 'clarity');
  const copies = screenCopies(payload, page);
  const clarityCopies = copies.filter((copy) =>
    copy.items.some((item) => item.source === 'clarity'),
  );
  return (
    <div className={`page-pulse page-pulse--${mode}`}>
      <div className="page-pulse-head">
        <div>
          <p>
            /pages/partnerships, /pages/start, /pages/corporategifting. GA4 refreshes every 4 hours;
            Clarity and Vidalytics are copied once a day. Each tab says when its data is as of. Last
            update {lastUpdate(payload)}.
          </p>
          <p className="page-pulse-partial">
            <Term label="Newest day may be partial." term="partial" />
          </p>
        </div>
        <div className="lead-filter-chips">
          {RANGES.map((range) => (
            <button
              className={`lead-filter-chip${days === range ? ' lead-filter-chip--on' : ''}`}
              type="button"
              aria-pressed={days === range}
              key={range}
              onClick={() => onDays(range)}
            >
              {range} days
            </button>
          ))}
          {shareSlot}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              const file = exportAll(payload, page);
              downloadPulseText(file.filename, file.csv);
            }}
          >
            Export all
          </Button>
        </div>
      </div>
      <div className="lead-subtabs" role="tablist" aria-label="Page Pulse sources">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`page-pulse-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`page-pulse-panel-${key}`}
            className={`lead-subtab${tab === key ? ' lead-subtab--on' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'vidalytics' ? null : (
        <div className="lead-filter-chips" aria-label="Pages">
          <button
            type="button"
            aria-pressed={page === 'all'}
            className={`lead-filter-chip${page === 'all' ? ' lead-filter-chip--on' : ''}`}
            onClick={() => setPage('all')}
          >
            All 3
          </button>
          {PAGES.map((item) => (
            <button
              key={item.path}
              type="button"
              aria-pressed={page === item.path}
              className={`lead-filter-chip${page === item.path ? ' lead-filter-chip--on' : ''}`}
              onClick={() => setPage(item.path)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
      <section
        id="page-pulse-panel-ga4"
        role="tabpanel"
        aria-labelledby="page-pulse-ga4"
        hidden={tab !== 'ga4'}
      >
        <h2 id="page-pulse-ga4">Google Analytics</h2>
        <p className="page-pulse-partial page-pulse-asof">{updatedAsOf(payload, 'ga4')}</p>
        {copies.map((copy) => (
          <Ga4Copy key={copy.page} copy={copy} showPage={page === 'all'} />
        ))}
        <Panel
          title="Reports snapshot"
          actions={<ExportButton name="page-pulse-kpis" rows={exportKpis(payload, page)} />}
        >
          <Tiles tiles={gaKpiTiles(payload, page)} />
          <ViewsChart series={series} />
        </Panel>
        <Panel
          title="Pages and screens"
          actions={<ExportButton name="page-pulse-pages" rows={exportPages(payload, page)} />}
        >
          <div className="page-pulse-table-scroll">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>
                    <Term label="Page path and screen class" term="pagePath" />
                  </th>
                  <th>
                    <Term label="Views" term="views" />
                  </th>
                  <th>
                    <Term label="Active users" term="activeUsers" />
                  </th>
                  <th>
                    <Term label="Views per active user" term="viewsPerActiveUser" />
                  </th>
                  <th>
                    <Term
                      label="Average engagement time per active user"
                      term="averageEngagementTime"
                    />
                  </th>
                  <th>
                    <Term label="Event count: form_start" term="eventName" />
                  </th>
                  <th>
                    <Term label="Event count: form_submit" term="eventCount" align="end" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {selected.map((item) => {
                  const views = total(payload, item.path, 'screenPageViews', 'ga4');
                  const users = total(payload, item.path, 'activeUsers', 'ga4');
                  return (
                    <tr key={item.path}>
                      <td>
                        {item.path}
                        {mode === 'hub' ? (
                          <div className="page-pulse-links">
                            {clarityProject !== '' ? (
                              <>
                                <a
                                  href={`https://clarity.microsoft.com/projects/view/${clarityProject}`}
                                >
                                  Open heatmap in Clarity
                                </a>
                                <a
                                  href={`https://clarity.microsoft.com/projects/view/${clarityProject}`}
                                >
                                  Watch recordings
                                </a>
                              </>
                            ) : null}
                            <a
                              href={`https://analytics.google.com/analytics/web/#/p${GA4_PROPERTY_ID}`}
                            >
                              Open in GA4
                            </a>
                          </div>
                        ) : null}
                      </td>
                      <td>{views}</td>
                      <td>{users}</td>
                      <td>{users === 0 ? '0.00' : (views / users).toFixed(2)}</td>
                      <td>
                        {ga4Duration(
                          total(payload, item.path, 'userEngagementDuration', 'ga4') /
                            Math.max(users, 1),
                        )}
                      </td>
                      <td>{total(payload, item.path, 'form_start', 'ga4')}</td>
                      <td>{total(payload, item.path, 'form_submit', 'ga4')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
        <Panel
          title="Traffic acquisition"
          actions={<ExportButton name="page-pulse-sources" rows={exportSources(payload, page)} />}
        >
          <TwoColumn
            name="Session source / medium"
            term="sourceMedium"
            rows={sources.map((row) => [row.label, row.sessions])}
          />
        </Panel>
        <Panel title="Tech overview">
          <TwoColumn
            name="Device category"
            term="deviceCategory"
            rows={breakdownRows(payload, page, 'device')}
          />
        </Panel>
        <Panel title="Demographic details">
          <TwoColumn name="Country" term="country" rows={breakdownRows(payload, page, 'country')} />
        </Panel>
        <Panel title="DM test">
          {!dm.live ? (
            <EmptyState
              title="Not live yet"
              description="UTM content is added when the DM links exist."
            />
          ) : (
            <div className="page-pulse-table-scroll">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Clicks</th>
                    <th>Submits</th>
                    <th>Calls booked</th>
                    <th>Leading</th>
                  </tr>
                </thead>
                <tbody>
                  {dm.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td>{row.sessions}</td>
                      <td>{row.form_submit}</td>
                      <td>--</td>
                      <td>{row.leading ? <Badge tone="success">Leading</Badge> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </section>
      <section
        id="page-pulse-panel-clarity"
        role="tabpanel"
        aria-labelledby="page-pulse-clarity"
        hidden={tab !== 'clarity'}
      >
        <h2 id="page-pulse-clarity">Clarity</h2>
        <p className="page-pulse-partial page-pulse-asof">{updatedAsOf(payload, 'clarity')}</p>
        {mode === 'hub' ? <ExternalLinks links={CLARITY_LINKS} /> : null}
        <p className="page-pulse-partial">
          {clarityHistoryNote(payload)}
          <InfoHint label="Clarity history" text={PULSE_DEFINITIONS.clarityHistory.text} />
        </p>
        {clarityCopies.map((copy) => (
          <ClarityCopy key={copy.page} copy={copy} showPage={page === 'all'} />
        ))}
        <Panel
          title="Dashboard"
          actions={<ExportButton name="page-pulse-clarity" rows={exportClarity(payload, page)} />}
        >
          <Tiles
            tiles={clarityTiles(payload, page)}
            terms={{ ...TILE_DEFINITIONS, Sessions: 'claritySessions' }}
          />
          <div className="tracker-kpi-grid">
            {CLARITY_INSIGHTS.map(([label, key, term], index) => {
              const count = total(payload, page, key, 'clarity');
              return (
                <div className="tracker-kpi-card" key={key}>
                  <p className="tracker-kpi-label">
                    <Term label={label} term={term} align={index % 2 === 1 ? 'end' : 'start'} />
                  </p>
                  <p className="tracker-kpi-value">{count}</p>
                  <p className="tracker-kpi-sub">
                    {Math.round((count / Math.max(claritySessions, 1)) * 100)}% of sessions
                  </p>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Smart events">
          <p className="page-pulse-partial">
            <Term label="Smart events" term="smartEvents" />
          </p>
          {smartEvents.length === 0 ? (
            <p>No data available</p>
          ) : (
            <TwoColumn
              name="Smart events"
              term="smartEvents"
              rows={smartEvents}
              valueTerm="claritySessions"
            />
          )}
        </Panel>
      </section>
      <section
        id="page-pulse-panel-vidalytics"
        role="tabpanel"
        aria-labelledby="page-pulse-vidalytics"
        hidden={tab !== 'vidalytics'}
      >
        <h2 id="page-pulse-vidalytics">Vidalytics</h2>
        <p className="page-pulse-partial page-pulse-asof">{updatedAsOf(payload, 'vidalytics')}</p>
        {mode === 'hub' ? <ExternalLinks links={VIDALYTICS_LINKS} /> : null}
        <VidalyticsTab payload={payload} />
      </section>
    </div>
  );
}
